import type { SalonRow } from '@manaro/contracts'
import { cn } from '@/shared/lib/cn.ts'
import { formatDate } from '@/shared/lib/format.ts'
import { Badge } from '@/shared/ui/badge.tsx'
import { DesktopTable, MobileCardList } from '@/shared/ui/responsive-table.tsx'
import { STAGE_LABELS, STAGE_TONES } from '../labels.ts'
import type { RowNotes } from '../notes.ts'
import { NotesCell } from './notes-cell.tsx'
import { SalonCard } from './salon-card.tsx'
import { Count, ModerationCell, OrdersCell, OwnerCell, PlanCell, SalonCell, SourceCell } from './salon-cells.tsx'

const LEADING_HEADERS = ['Регистрация', 'Откуда', 'Владелец', 'Салон']
const TRAILING_HEADERS = ['Услуг', 'Клиентов', 'Записи', 'Тариф', 'Стадия']
// Right after the salon: what the team agreed with it is read together with who it is.
const NOTES_HEADER = 'Комментарии'
const MODERATION_HEADER = 'Каталог'

export const rowKey = (row: SalonRow): string => `${row.user.id}-${row.salon?.id ?? 0}`

interface SalonsTableProps {
  rows: SalonRow[]
  /** Set when the backend supports moderation: adds the catalog column and the review entry point. */
  onModerate?: (row: SalonRow) => void
  /** Set for the service owners: a free period of a paid plan from the plan column. */
  onGrant?: (salonId: number) => void
  notes?: RowNotes
}

export function SalonsTable({ rows, onModerate, onGrant, notes }: SalonsTableProps) {
  const headers = [
    ...LEADING_HEADERS,
    ...(notes ? [NOTES_HEADER] : []),
    ...TRAILING_HEADERS,
    ...(onModerate ? [MODERATION_HEADER] : []),
  ]

  return (
    <>
      <MobileCardList label="Салоны">
        {rows.map((row) => (
          <SalonCard key={rowKey(row)} row={row} onModerate={onModerate} onGrant={onGrant} notes={notes} />
        ))}
      </MobileCardList>

      <DesktopTable>
        <table
          className={cn(
            'w-full text-left text-[13.5px]',
            onModerate ? (notes ? 'min-w-[1460px]' : 'min-w-[1240px]') : notes ? 'min-w-[1320px]' : 'min-w-[1100px]',
          )}
        >
          <thead className="border-b border-line-soft text-[12px] text-ink-muted">
            <tr>
              {headers.map((header) => (
                <th key={header} scope="col" className="px-3.5 py-3 font-medium whitespace-nowrap">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft align-top">
            {rows.map((row) => (
              <tr key={rowKey(row)} className="hover:bg-surface-hover/50">
                <td className="px-3.5 py-3 whitespace-nowrap">
                  {row.user.registeredAt ? formatDate(row.user.registeredAt) : '—'}
                </td>
                <td className="max-w-[220px] min-w-[190px] px-3.5 py-3">
                  <SourceCell user={row.user} />
                </td>
                <td className="px-3.5 py-3">
                  <OwnerCell user={row.user} chats />
                </td>
                <td className="max-w-[240px] min-w-[200px] px-3.5 py-3">
                  <SalonCell salon={row.salon} />
                </td>
                {notes && (
                  <td className="w-[230px] max-w-[260px] min-w-[200px] px-3.5 py-3">
                    <NotesCell notes={notes.forRow(row)} onOpen={() => notes.onOpen(row)} />
                  </td>
                )}
                <td className="px-3.5 py-3">
                  <Count value={row.salon?.services} />
                </td>
                <td className="px-3.5 py-3">
                  <Count value={row.salon?.customers} />
                </td>
                <td className="w-[140px] px-3.5 py-3">
                  <OrdersCell salon={row.salon} />
                </td>
                <td className="px-3.5 py-3 whitespace-nowrap">
                  <PlanCell salon={row.salon} onGrant={onGrant && row.salon ? onGrant.bind(null, row.salon.id) : undefined} />
                </td>
                <td className="px-3.5 py-3 whitespace-nowrap">
                  <Badge tone={STAGE_TONES[row.stage]}>{STAGE_LABELS[row.stage]}</Badge>
                </td>
                {onModerate && (
                  <td className="px-3.5 py-3">
                    <ModerationCell salon={row.salon} onOpen={() => onModerate(row)} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </DesktopTable>
    </>
  )
}
